/* eslint-disable no-unused-vars */
import types from '../types'
import { v4 as uuidv4 } from 'uuid'

const initialState = {
  isFetching: false,
  error: false,
  isSuccess: false,
  opportunities: [],
  rules: [],
  actions: [],
  subActions: [],
  saveCarePlan: {
    loading: false,
    error: false,
    success: false
  },
  editingNote: false,
  createAssembledNote: {
    loading: false,
    error: false,
    success: false
  },
  assembledNote: ''
}

const ccpReducer = (state = initialState, action) => {
  switch (action.type) {
  case types.SET_CAREPLAN_REQUESTED: {
    return ({
      ...state,
      isFetching: true,
      error: false,
      isSuccess: false,
      opportunities: [],
      rules: [],
      actions: []
    })
  }
  case types.SET_CAREPLAN_FULFILLED: {
    const { opportunities, rules } = action.payload

    const newOpportunities = opportunities.map((opportunity) => {
      const opportunityRules = rules.filter((rule) => rule.opportunityId === opportunity.opportunityId)
      const newActiveRuleIds = opportunityRules.reduce((acc, curr) => {
        if (curr.value) {
          return [...acc, curr.ruleId]
        }
        return acc
      }, [])
      return {
        ...opportunity,
        ruleIds: opportunityRules.map((rule) => rule.ruleId),
        activeRuleIds: newActiveRuleIds,
        checked: newActiveRuleIds.length > 0
      }
    })


    const newRules = []
    const newActions = []
    const newSubActions = []
    for (let i = 0; i < rules.length; i += 1) {
      const rule = rules[i]
      const actionIds = []
      for (let k = 0; k < rule.ruleActions.length; k += 1) {
        const ruleAction = rule.ruleActions[k]

        const actionKey = uuidv4()
        actionIds.push(actionKey)

        const subActionIds = []

        for (let j = 0; j < ruleAction.action.children.length; j += 1) {
          const subAction = ruleAction.action.children[j]

          let subActionKey
          if (!newSubActions.some((action) => action.actionId === subAction.actionId)) {
            subActionKey = uuidv4()
            newSubActions.push({
              key: subActionKey,
              actionId: subAction.actionId,
              actionType: subAction.actionType.name,
              name: subAction.name,
              description: subAction.description,
              orderKey: subAction.orderKey
            })
          } else {
            const foundSubaction = newSubActions.find((action) => action.actionId === subAction.actionId)
            subActionKey = foundSubaction.key
          }

          subActionIds.push(subActionKey)
        }

        newActions.push({
          key: actionKey,
          actionId: ruleAction.actionId,
          actionType: ruleAction.action.actionType.name,
          name: ruleAction.action.name,
          description: ruleAction.action.description,
          orderKey: ruleAction.action.orderKey,
          ordered: false,
          subActionIds,
          isRecommended: rule.value ? true : false,
          isChanged: false
        })
      }

      newRules.push({
        ruleId: rule.ruleId,
        name: rule.ruleDescription,
        shortName: rule.shortRuleDescription,
        actionIds,
        activeActionIds: rule.value ? actionIds : [],
        activeSubActionIds: [],
        clinicalContent: rule.ruleContent.filter((ruleContent) => ruleContent.content.contentTypeId === 8),
        patientEdContent: rule.ruleContent.filter((ruleContent) => ruleContent.content.contentTypeId === 7),
        isRecommended: rule.value ? true : false,
        isChanged: false
      })
    }

    return ({
      ...state,
      isFetching: false,
      isSuccess: true,
      opportunities: newOpportunities,
      rules: newRules,
      actions: newActions,
      subActions: newSubActions
    })
  }
  case types.GET_CAREPLAN_FULFILLED: {
    const { opportunities, patientOpportunities, rules, patientActions, recommendations } = action.payload
    const newActions = rules.reduce((acc, curr) => [...acc, ...curr.ruleActions], []).map((ruleAction) => {
      return {
        actionId: ruleAction.actionId,
        actionType: ruleAction.action.actionType.name,
        name: ruleAction.action.name,
        description: ruleAction.action.description
      }
    })
    const newRules = rules.map((rule) => {
      const actionIds = rule.ruleActions.map((ruleAction) => ruleAction.actionId)
      const foundRecommendation = recommendations.find((recommendation) => recommendation.ruleId === rule.ruleId)
      return {
        ruleId: rule.ruleId,
        name: rule.description,
        actionIds,
        activeActionIds: foundRecommendation !== undefined ? actionIds.filter((actionId) => patientActions.find((patientAction) => patientAction.actionId === actionId) !== undefined) : []
      }
    })

    const newOpportunities = opportunities.map((opportunity) => {
      const patientOpportunity = patientOpportunities.find((patientOpp) => patientOpp.opportunityId === opportunity.opportunityId)
      const ruleIds = rules.filter((rule) => rule.opportunityId === opportunity.opportunityId).map((rule) => rule.ruleId)

      const activeRuleIds = ruleIds.filter((ruleId) => recommendations.find((recommendation) => recommendation.ruleId === ruleId) !== undefined)
      return {
        ...opportunity,
        ruleIds,
        activeRuleIds,
        checked: patientOpportunity !== undefined
      }
    })

    return ({
      ...state,
      isFetching: false,
      isSuccess: true,
      opportunities: newOpportunities,
      rules: newRules,
      actions: newActions
    })
  }
  case types.SET_CAREPLAN_REJECTED: {
    return ({
      ...state,
      isFetching: false,
      error: action.payload
    })
  }
  case types.UPDATE_CAREPLAN_OPPORTUNITY: {
    const { name, checked } = action.payload
    const index = state.opportunities.findIndex((opportunity) => opportunity.opportunityId === parseInt(name, 10))
    const newOpportunity = state.opportunities[index]

    let newRules = state.rules
    const newActions = state.actions.map((action) => ({
      ...action,
      isChanged: !checked && action.isRecommended ? true : checked ? action.isChanged : false
    }))

    newOpportunity.ruleIds.forEach((ruleId) => {
      const ruleIndex = newRules.findIndex((rule) => ruleId === rule.ruleId)
      const rule = newRules[ruleIndex]
      newRules = [
        ...newRules.slice(0, ruleIndex),
        {
          ...rule,
          activeActionIds: [],
          activeSubActionIds: [],
          isChanged: !checked && rule.isRecommended ? true : checked ? rule.isChanged : false
        },
        ...newRules.slice(ruleIndex + 1)
      ]
    })

    return ({
      ...state,
      opportunities: [
        ...state.opportunities.slice(0, index),
        {
          ...newOpportunity,
          activeRuleIds: [],
          checked
        },
        ...state.opportunities.slice(index + 1)
      ],
      rules: newRules,
      actions: newActions
    })
  }
  case types.UPDATE_CAREPLAN_RULE: {
    const { name, checked } = action.payload
    const index = state.rules.findIndex((rule) => rule.ruleId === parseInt(name, 10))
    const newRule = state.rules[index]

    const newActions = state.actions.map((action) => {
      const foundAction = checked ? newRule.actionIds.find((actionId) => actionId === action.key)
        : newRule.activeActionIds.find((activeActionId) => activeActionId === action.key)

      if (foundAction) {
        return {
          ...action,
          activeSubActionIds: checked ? action.activeSubActionIds : [],
          isChanged: action.isRecommended ? !checked : checked
        }
      }

      return action
    })

    const newOpportunity = state.opportunities.find((opportunity) => opportunity.ruleIds.includes(newRule.ruleId))
    const opportunityIndex = state.opportunities.findIndex((opportunity) => opportunity.opportunityId === newOpportunity.opportunityId)
    const opportunityActiveRuleIds = checked ? [...newOpportunity.activeRuleIds, parseInt(name, 10)] : newOpportunity.activeRuleIds.filter((ruleId) => ruleId !== parseInt(name, 10))
    return ({
      ...state,
      rules: [
        ...state.rules.slice(0, index),
        {
          ...newRule,
          activeActionIds: checked ? newRule.actionIds : [],
          activeSubActionIds: checked ? newRule.activeSubActionIds : [],
          isChanged: newRule.isRecommended ? !checked : checked
        },
        ...state.rules.slice(index + 1)
      ],
      opportunities: [
        ...state.opportunities.slice(0, opportunityIndex),
        {
          ...newOpportunity,
          activeRuleIds: opportunityActiveRuleIds,
          checked: opportunityActiveRuleIds.length > 0
        },
        ...state.opportunities.slice(opportunityIndex + 1)
      ],
      actions: newActions
    })
  }
  case types.UPDATE_CAREPLAN_ACTION: {
    const { target: { checked, name }, ruleId } = action.payload

    const actionIndex = state.actions.findIndex((action) => action.key === name)
    const newAction = state.actions[actionIndex]
    const newActions = [
      ...state.actions.slice(0, actionIndex),
      {
        ...newAction,
        isChanged: newAction.isRecommended ? !checked : checked
      },
      ...state.actions.slice(actionIndex + 1)
    ]

    const ruleIndex = state.rules.findIndex((rule) => rule.ruleId === ruleId)
    const newRule = state.rules[ruleIndex]
    const newRuleActions = checked ? [...newRule.activeActionIds, name] : newRule.activeActionIds.filter((actionId) => actionId !== name)

    const opportunityIndex = state.opportunities.findIndex((opportunity) => opportunity.ruleIds.includes(newRule.ruleId))
    const newOpportunity = state.opportunities[opportunityIndex]
    if (!checked && newRuleActions.length === 0) {
      const activeRuleIds = newOpportunity.activeRuleIds.filter((ruleId) => ruleId !== newRule.ruleId)
      newOpportunity.activeRuleIds = newOpportunity.activeRuleIds.filter((ruleId) => ruleId !== newRule.ruleId)
      newOpportunity.checked = activeRuleIds.length > 0
    }

    if (checked && newRule.activeActionIds.length === 0) {
      newOpportunity.activeRuleIds = [...newOpportunity.activeRuleIds, newRule.ruleId]
    }


    return ({
      ...state,
      rules: [
        ...state.rules.slice(0, ruleIndex),
        {
          ...newRule,
          activeActionIds: newRuleActions,
          activeSubActionIds: checked ? state.rules[ruleIndex].activeSubActionIds : [],
          isChanged: newRule.isRecommended ? newRuleActions.length === 0 : !checked && newRuleActions.length !== 0
        },
        ...state.rules.slice(ruleIndex + 1)
      ],
      opportunities: [
        ...state.opportunities.slice(0, opportunityIndex),
        newOpportunity,
        ...state.opportunities.slice(opportunityIndex + 1)
      ],
      actions: newActions
    })
  }
  case types.UPDATE_CAREPLAN_SUB_ACTION: {
    const { target: { checked, name }, ruleId, actionId } = action.payload

    const foundAction = state.actions.find((a) => a.actionId === actionId)

    const ruleIndex = state.rules.findIndex((rule) => rule.ruleId === ruleId)
    const newRule = state.rules[ruleIndex]
    const newSubActions = checked ? [...newRule.activeSubActionIds, name] : newRule.activeSubActionIds.filter((actionId) => actionId !== name)

    return ({
      ...state,
      rules: [
        ...state.rules.slice(0, ruleIndex),
        {
          ...newRule,
          activeSubActionIds: newSubActions,
        },
        ...state.rules.slice(ruleIndex + 1)
      ]
    })
  }
  case types.UPDATE_WRAP_UP_ACTION: {
    const { target: { checked }, action: patientAction } = action.payload

    let newActions = state.actions
    let newRules = state.rules
    let newOpportunities = state.opportunities

    if (!patientAction.isSubAction) {
      newActions = newActions.reduce((acc, curr) => {
        if (patientAction.keys.includes(curr.key)) {
          return [
            ...acc,
            {
              ...curr,
              isChanged: curr.isRecommended ? !checked : checked
            }
          ]
        }

        return [...acc, curr]
      }, [])

      const rules = state.rules.filter((rule) => rule.actionIds.some((actionKey) => patientAction.keys.includes(actionKey)))

      for (let i = 0; i < rules.length; i += 1) {
        const rule = rules[i]
        const ruleIndex = state.rules.findIndex((r) => r.ruleId === rule.ruleId)
        let newRuleActions = rule.activeActionIds
        newRuleActions = checked ? [...newRuleActions, patientAction.key] : newRuleActions.filter((actionId) => actionId !== patientAction.key)


        let isChanged = rule.isChanged
        if (rule.isChanged || rule.isRecommended) {
          isChanged = newRuleActions.length === 0
        }

        newRules = [
          ...newRules.slice(0, ruleIndex),
          {
            ...rule,
            activeActionIds: newRuleActions,
            isChanged,
          },
          ...newRules.slice(ruleIndex + 1)
        ]

        const opportunityIndex = newOpportunities.findIndex((opportunity) => opportunity.ruleIds.includes(rule.ruleId))
        const newOpportunity = newOpportunities[opportunityIndex]
        if (!checked && newRuleActions.length === 0) {
          const activeRuleIds = newOpportunity.activeRuleIds.filter((ruleId) => ruleId !== rule.ruleId)
          newOpportunity.activeRuleIds = newOpportunity.activeRuleIds.filter((ruleId) => ruleId !== rule.ruleId)
          newOpportunity.checked = activeRuleIds.length > 0
        }

        if (checked && rule.activeActionIds.length === 0) {
          newOpportunity.activeRuleIds = [...newOpportunity.activeRuleIds, rule.ruleId]
        }

        newOpportunities = [
          ...newOpportunities.slice(0, opportunityIndex),
          newOpportunity,
          ...newOpportunities.slice(opportunityIndex + 1)
        ]
      }
    } else {
      const actions = newActions.filter((newAction) => newAction.subActionIds.some((subActionId) => patientAction.keys.includes(subActionId)) && (newAction.isRecommended !== newAction.isChanged))
      for (let i = 0; i < actions.length; i += 1) {
        const action = actions[i]
        const rules = newRules.filter((rule) => rule.actionIds.includes(action.key))
        for (let k = 0; k < rules.length; k += 1) {
          const rule = rules[k]
          const ruleIndex = state.rules.findIndex((r) => r.ruleId === rule.ruleId)
          const newSubActionIds = checked ? [...rule.activeSubActionIds, patientAction.key] : rule.activeSubActionIds.filter((actionId) => actionId !== patientAction.key)

          newRules = [
            ...newRules.slice(0, ruleIndex),
            {
              ...rule,
              activeSubActionIds: newSubActionIds
            },
            ...newRules.slice(ruleIndex + 1)
          ]
        }
      }
    }

    return {
      ...state,
      opportunities: newOpportunities,
      rules: newRules,
      actions: newActions
    }
  }
  case types.SAVE_CAREPLAN_REQUESTED: {
    return {
      ...state,
      saveCarePlan: {
        ...state.saveCarePlan,
        loading: true,
        error: false,
        success: false
      }
    }
  }
  case types.SAVE_CAREPLAN_FULFILLED: {
    return {
      ...state,
      saveCarePlan: {
        ...state.saveCarePlan,
        loading: false,
        success: true
      }
    }
  }
  case types.SAVE_CAREPLAN_REJECTED: {
    return {
      ...state,
      saveCarePlan: {
        ...state.saveCarePlan,
        loading: false,
        error: action.payload
      }
    }
  }
  case types.CLEAR_CAREPLAN: {
    return {
      ...state,
      opportunities: state.opportunities.map((opportunity) => ({
        ...opportunity,
        activeRuleIds: [],
        checked: false
      })),
      rules: state.rules.map((rule) => ({
        ...rule,
        activeActionIds: []
      }))
    }
  }
  case types.CANCEL_NOTE_EDIT: {
    const { assembledNote, note } = action.payload
    return {
      ...state,
      note,
      assembledNote
    }
  }
  case types.SET_EDITING_NOTE: {
    return {
      ...state,
      editingNote: action.payload
    }
  }
  case types.UPDATE_WRAP_UP_NOTE: {
    const { key, id, value } = action.payload
    const index = state.note[key].findIndex((note) => note.id === id)
    return {
      ...state,
      note: {
        ...state.note,
        [key]: [
          ...state.note[key].slice(0, index),
          {
            ...state.note[key][index],
            message: value
          },
          ...state.note[key].slice(index + 1)
        ]
      }
    }
  }
  case types.CREATE_WRAP_UP_NOTE: {
    return {
      ...state,
      note: action.payload
    }
  }
  case types.CREATE_ASSEMBLED_NOTE_REQUESTED: {
    return {
      ...state,
      createAssembledNote: {
        loading: true,
        success: false,
        error: false
      }
    }
  }
  case types.CREATE_ASSEMBLED_NOTE_FULFILLED: {
    return {
      ...state,
      createAssembledNote: {
        loading: false,
        success: true,
        error: false
      },
      assembledNote: action.payload
    }
  }
  case types.CREATE_ASSEMBLED_NOTE_REJECTED: {
    return {
      ...state,
      createAssembledNote: {
        loading: false,
        success: false,
        error: action.payload
      }
    }
  }
  case types.UPDATE_ASSEMBLED_NOTE: {
    return {
      ...state,
      assembledNote: action.payload
    }
  }
  default:
    return state
  }
}

export default ccpReducer
